import React, { isValidElement, ReactNode, useState } from "react"
import { useTheme } from "~/ui/styled"
import { observer } from "mobx-react-lite"
import { ColumnFlex, CenterFlex, BetweenFlex, Text, Button } from "~components/El"
import { Renderable, ModalContentProps } from "./types"
import { usePopup } from "~hooks/usePopup"

type ActionHandler = (() => void | Promise<void>) | undefined

const renderTitle = (title?: Renderable): React.ReactNode => {
  const theme = useTheme()

  if (title == null) return null

  if (typeof title === 'string' || typeof title === 'number') {
    return (
      <Text
        style={{ color: theme.colors.text }}
        fontSize={theme.fontSizes.lg}
        fontWeight="600"
      >
        {title}
      </Text>
    )
  }

  if (React.isValidElement(title)) {
    return title
  }

  if (typeof title === 'function') {
    return renderTitle(title())
  }

  return null
}

const renderContent = (content?: Renderable): ReactNode => {
  const theme = useTheme()
  if (typeof content === 'string' || typeof content === 'number') {
    return <Text style={{ color: theme.colors.text }} fontSize={theme.fontSizes.md} lineHeight="22px">{content}</Text>
  }

  if (isValidElement(content)) {
    return content
  }

  if (typeof content === 'function') {
    try {
      const result = content()
      // ⚡递归调用，保证函数返回值也能被正常处理
      return renderContent(result)
    } catch (err) {
      return null
    }
  }

  return null
}

const ModalContent: React.FC<ModalContentProps> = ({
    title = '',
    content,
    loadingText = '',
    confirmText = '确定',
    cancelText = '取消',
    closeOnAction = true,
    confirmOnAction = true,
    disabled = false,
    autoClose = true,
    popupId,
    onCancel,
    onConfirm,
}) => {
    const theme = useTheme()
    const popup = usePopup()
    const [loading, setLoading] = useState(false)

    const closePopup = () => {
      if (popupId != null) popup.close(popupId)
      else popup.close()
    }

    // 通用点击逻辑
    const handleAction = async (actionFn?: ActionHandler) => {
      if (!actionFn) {
        if (autoClose) {
          closePopup()
        }
        return
      }

      try {
        const ret = actionFn()

        if (ret instanceof Promise) {
          setLoading(true)
          await ret
          setLoading(false)
          if (autoClose) {
            closePopup()
          }
        } else {
          if (autoClose) {
            closePopup()
          }
        }
      } catch {
        setLoading(false)
      }
    }

    return (
        <ColumnFlex
            width="100%"
            padding="20px"
            borderRadius="20px"
            bg={theme.colors.blockBackground}
        >
            {title && (
                <CenterFlex width="100%">
                    {renderTitle(title)}
                </CenterFlex>
            )}
            <CenterFlex width="100%" padding="20px 0">
                {renderContent(content)}
            </CenterFlex>
            <BetweenFlex width="100%">
                {closeOnAction && (
                    <Button
                        flex={1}
                        height="44px"
                        margin="0 6px"
                        borderRadius="8px"
                        bg={theme.colors.background}
                        onClick={() => handleAction(onCancel)}
                    >
                    <Text fontSize={theme.fontSizes.lg} color={theme.colors.secondary}>{cancelText}</Text>
                    </Button>
                )}
                {confirmOnAction && (
                    <Button
                        flex={1}
                        height="44px"
                        margin="0 6px"
                        borderRadius="8px"
                        bg={theme.colors.primary}
                        loading={loading}
                        loadingText={loadingText}
                        disabled={disabled}
                        onClick={() => handleAction(onConfirm)}
                    >
                    <Text fontSize={theme.fontSizes.lg} color={theme.colors.textLight} fontWeight="600">{confirmText}</Text>
                    </Button>
                )}
                
            </BetweenFlex>
        </ColumnFlex>
    )

}
export const ObservedModalContent = observer(ModalContent)
